import React, { useMemo } from 'react';
import { GeoSelect, type GeoOption } from './GeoSelect';
import { COUNTRY_DIALING_CODES, countryName, getCountryFlagEmoji } from '../../utils/phoneCountryCodes';

export interface CountryPhoneSelectProps {
  /** Unique id for the trigger button. */
  id: string;
  /** Current dialing code (e.g. '+34'). */
  value: string;
  /** Called with the selected dialing code. */
  onChange: (dialingCode: string) => void;
  /** Disable interaction. */
  disabled?: boolean;
  /** Borderless trigger (filled background) — matches inline form fields. */
  borderless?: boolean;
  /** Denser layout for inline use in a compact editor row. */
  compact?: boolean;
  /** Placeholder when no code selected. */
  placeholder?: string;
  /** Extra classes appended to the trigger button to match a target input
   *  style (underline-only, specific height, transparent bg, …). */
  triggerClassName?: string;
  /**
   * Custom trigger formatter. Defaults to showing only the flag + dialing code
   * (no country name). Pass a function to customise further.
   */
  formatTrigger?: (option: GeoOption | null) => string;
}

/**
 * Reusable searchable country dropdown that carries each country's phone
 * dialing code. Built on the GeoSelect atom (search, keyboard nav, flag emoji)
 * and the shared COUNTRY_DIALING_CODES map. Shows a flag + country name in the
 * list with the dialing code as a muted hint, so the international phone picker
 * is searchable instead of an unwieldy long dropdown.
 */

/** Dialing codes shared by several countries resolve to a PREFERRED flag when
 *  only the code is known (the number's area code could disambiguate further,
 *  but that needs a full NANP-style plan). Without this, GeoSelect's
 *  first-match on the alphabetically-sorted option list picked Canada for
 *  every +1 number and Kazakhstan for every +7. */
const PREFERRED_ISO_BY_DIALING_CODE: Record<string, string> = {
  '+1': 'US', // NANP — US/Canada share +1; US is the platform's default read
  '+7': 'RU', // Russia/Kazakhstan share +7; RU dominates by lead volume
  '+44': 'GB', // GB/UK are the same territory — keep the flag stable
};
export const CountryPhoneSelect: React.FC<CountryPhoneSelectProps> = ({
  id,
  value,
  onChange,
  disabled = false,
  borderless = false,
  compact = false,
  placeholder = 'Select…',
  triggerClassName = '',
  formatTrigger,
}) => {
  // Default trigger: flag + dialing code only (no country name).
  const defaultTrigger = (option: GeoOption | null): string =>
    option ? `${option.emoji || ''} ${option.hint || option.value}`.trim() : '';

  const options = useMemo<GeoOption[]>(() => {
    const all = Object.entries(COUNTRY_DIALING_CODES).map(([iso, dialing]) => ({
      iso,
      dialing,
      label: countryName(iso),
    }));
    // One option per DIALING CODE (not per ISO): ambiguous codes keep only
    // their preferred country so GeoSelect's value-lookup resolves to it.
    // The preferred country is chosen deterministically instead of by
    // alphabetical accident (Canada sorted before United States, so +1
    // always showed the Canadian flag on US numbers).
    const byDialing = new Map<string, { iso: string; dialing: string; label: string }>();
    for (const entry of all) {
      const preferredIso = PREFERRED_ISO_BY_DIALING_CODE[entry.dialing];
      if (preferredIso && entry.iso !== preferredIso) continue;
      if (byDialing.has(entry.dialing)) continue;
      byDialing.set(entry.dialing, entry);
    }
    return [...byDialing.values()]
      .map(({ dialing, label, iso }) => ({
        value: dialing,
        label,
        emoji: getCountryFlagEmoji(iso),
        hint: dialing,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  return (
    <GeoSelect
      id={id}
      label=""
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      borderless={borderless}
      compact={compact}
      triggerClassName={triggerClassName}
      formatTrigger={formatTrigger ?? defaultTrigger}
    />
  );
};
